/**
 * The Bexio workspace navigation, declared once.
 *
 * Both places that show Bexio navigation read this list: the workspace tab bar
 * in `BexioLayout` and the sidebar's Bexio group. Two hand-maintained copies is
 * how a sidebar ends up offering a page the tab bar does not, or highlighting a
 * different one — the same failure `workOrganizationNav` exists to prevent for
 * the calendar links.
 *
 * ONLY routes that exist in `AppRouter` belong here. A label without a working
 * target is a promise the product does not keep, so the list grows when a page
 * ships, not when a design for it is approved. Adding one here makes it appear
 * in the tab bar and the sidebar at once.
 *
 * `end` mirrors React Router's `NavLink` prop. It matters for the workspace
 * root: `NavLink` matches on path PREFIX by default, so without `end` the
 * "Übersicht" entry (`/admin/bexio`) also reports itself active while the user
 * is on `/admin/bexio/drafts`, and the navigation claims they are in two places
 * at once. Every future entry nested under the root needs `end` only if it in
 * turn gains child routes.
 */

/** The workspace root. Also the "Übersicht" target. */
export const BEXIO_ROOT = "/admin/bexio";

/**
 * @typedef {Object} BexioNavItem
 * @property {string} key    stable identifier (test/react key; not shown)
 * @property {string} label  visible German label
 * @property {string} to     real, routed path — no placeholders
 * @property {string} icon   emoji used by the sidebar rows only
 * @property {boolean} [end] exact-match only (see module docstring)
 */

/** @type {BexioNavItem[]} */
export const BEXIO_NAV_ITEMS = [
  { key: "overview", label: "Übersicht", to: BEXIO_ROOT, icon: "🔗", end: true },
  {
    key: "drafts",
    label: "Rechnungsentwürfe",
    to: `${BEXIO_ROOT}/drafts`,
    icon: "🧾",
  },
  // "Rechnungen" is the mirrored Bexio invoice list — a different thing from
  // "Rechnungsentwürfe" above, which is the C5 draft-creation workflow. Keeping
  // both labels is deliberate: they are two stages, not two views of one list.
  {
    key: "invoices",
    label: "Rechnungen",
    to: `${BEXIO_ROOT}/invoices`,
    icon: "📄",
  },
  {
    key: "contacts",
    label: "Kontakte",
    to: `${BEXIO_ROOT}/contacts`,
    icon: "👥",
  },
  // The read-only reconciliation control surface. "Zahlungsabgleich" rather than
  // "Zahlungen": nothing here books a payment — it reconciles what Bexio reports
  // against the tenancies the invoices are mapped to.
  {
    key: "reconciliation",
    label: "Zahlungsabgleich",
    to: `${BEXIO_ROOT}/reconciliation`,
    icon: "⚖️",
  },
  // Last, because it is the workspace's operational log rather than one of the
  // data surfaces above it: an operator comes here to ask whether syncing is
  // working, not to look something up.
  {
    key: "sync-runs",
    label: "Sync-Läufe",
    to: `${BEXIO_ROOT}/sync-runs`,
    icon: "🔄",
  },
  // Last, after the operational log: this is where an operator goes to check
  // what the workspace is configured with, not to look up or monitor anything.
  // Read-only for now — it reports the stored Bexio invoice configuration and
  // says which parts of it are still missing.
  {
    key: "settings",
    label: "Einstellungen",
    to: `${BEXIO_ROOT}/settings`,
    icon: "⚙️",
  },
];

/**
 * Is the given pathname anywhere inside the Bexio workspace?
 *
 * Used by the sidebar to open its Bexio group when the user is already in it.
 * Prefix matching is correct HERE (unlike for a single nav entry): the question
 * is about the whole workspace, not about one page within it. The `/` guard
 * stops a sibling route such as `/admin/bexiofoo` from counting.
 */
export function isBexioPath(pathname) {
  const p = String(pathname || "");
  return p === BEXIO_ROOT || p.startsWith(`${BEXIO_ROOT}/`);
}
